import { mergeRight } from 'ramda'
import React, { memo, useEffect, useMemo, useContext, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { stringify, parse } from 'query-string'
import { DeleteRed, MarkAsRead, PillClose } from '@/components/icons'
import LoadingIndicator from '@/components/loading-indicator'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import { Box, Stack } from '@/components/structure'
import { Tour } from '@/components/tour'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import useDelayedMount from '@/hooks/use-delayed-mount'
import useLocalStorage from '@/hooks/use-localstorage'
import { useHighlight } from '@/models/toaster/hooks'
import { colors, breakpoints, structure } from '@/styles'
import capitalizeSentence from '@/utils/capitalize-sentence'
import { BulkContext } from '@/pages/Chat/context'
import { showDeleteConfirmationModal } from '@/features/Chat/chat-side-bar/delete-modal'
import { showMarkAsReadConfirmationModal } from '@/features/Chat/chat-side-bar/mark-as-read-modal'
import SideBarSearch from './side-bar-search'
import ChatCardsContainer from './chat-cards-container'
import ChatCardUser from './chat-card-user'
import { ChatContext } from './chat-context'
import { ChatCardSkeleton } from './chat-card-skeleton'
// Disabling slide animation for iOS for now
// const platform = isIOS() ? 'iOS' : ''
const platform = 'platform'

// 102vw is the magic number and should be addressed. TODO: refactor
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${colors.grey};
  margin-right: 1px;
  background-color: ${colors.white};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 102vw;
    min-width: unset;
    max-width: unset;
    border-right: none;
  }
`

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.grey};
`

const SearchContainer = styled.div`
  display: flex;
`

const SideBarWrapper = styled.div`
  display: flex;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    position: absolute;
    left: ${props => (props.isOpen ? '-10px' : 'calc(-100vw - 20px)')};
    top: ${props => props.top || 0}px;
    bottom: 0px;
    z-index: 99;
    background-color: ${colors.white};
    box-shadow: 1px 0px 6px rgba(0, 0, 0, 0.2);
    height: calc(100vh - ${structure.header.heightMobile}px);

    &.iOS {
      ${props =>
        props.navigationState === true &&
        (props =>
          props.isOpen
            ? 'animation: slideInChatSideBar 0.7s forwards'
            : 'animation: slideOutChatSideBar 0.7s forwards')};
    }
  }

  @keyframes slideOutChatSideBar {
    0% {
      left: -10px;
    }
    100% {
      left: -105%;
    }
  }

  @keyframes slideInChatSideBar {
    0% {
      left: -10px;
    }
    10% {
      left: -100%;
    }
    100% {
      left: -10px;
    }
  }
`

const NoChatsText = styled.h2`
  margin-left: 20px;
  margin-top: 50px;
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  top: 60px;
  left: 0px;
  transform: scale(0.5);
  z-index: 11;
  width: 100%;
  display: flex;
  justify-content: center;
`

const ChatTarget = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: calc(100vh - 66px);
  width: 350px;
  pointer-events: none;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    position: fixed;
    width: 100vw;
  }
`

const TagPillsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 15px 12px 20px;
`

const TagPill = styled.div`
  display: flex;
  padding: 4px 8px;
  padding-right: 11px;
  border-radius: 16px;
  background-color: ${colors.grey96};
`

const PillText = styled.span`
  color: ${colors.grey32};
  font-size: 14px;
`

const PillCloseWrapper = styled.span`
  cursor: pointer;
  margin-left: 7px;
`

const BulkActionMenuWrapper = styled.div`
  position: fixed;
  display: none;
  background-color: ${colors.grey96};
  width: 102vw;
  height: 80px;
  padding: 16px 16px 16px 26px;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
  bottom: 0;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: flex;
    gap: 10px;
  }
`

const BulkActionMenuButton = styled.div`
  background-color: ${colors.white};
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 48px;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.blue16};
  cursor: pointer;

  svg {
    margin-right: 10px;
  }
`

const BulkActionMenuButtonDelete = styled(BulkActionMenuButton)`
  border: 1.5px solid ${colors.red88};
  background-color: ${colors.red96};
  color: ${colors.red64};
`

const getFilterString = newObj => {
  return (
    '?' +
    stringify(newObj, {
      skipNull: true
    })
  )
}

const getParams = search => {
  return parse(search)
}
const renderChatCardSkeletons = () => {
  const arr = [1, 2, 3, 4, 5]
  return arr.map(i => <ChatCardSkeleton key={`chat-card-skeleton-${i}`} />)
}

const ChatSideBar = React.forwardRef(
  (
    {
      isOpen,
      closeNav,
      top,
      data = [],
      onEndReached = () => {},
      sideBarHeader = null,
      chatFooterButton = null,
      loading,
      isTourRedirect
    },
    ref
  ) => {
    const { t } = useTranslation()
    let history = useHistory()
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
    const role = useRole()
    const isLandlord = role === 'landlord'
    const location = useLocation()
    const dispatch = useDispatch()
    const { isSearchMode } = useContext(ChatContext)
    const bulkContext = useContext(BulkContext)
    const {
      selectMode,
      setSelectMode,
      selectedChat,
      setIsCheckAll,
      setBulkResult,
      navigationState
    } = bulkContext
    const isSearch = !!getParams(location.search)?.search
    const hasSideBarHeader = !!sideBarHeader
    const HeaderComponent = sideBarHeader
    const FooterButton = chatFooterButton
    const [, setKeyboardHasBeenClosed] = useLocalStorage('keyboardHasBeenClosed')

    useEffect(() => {
      if (location?.pathname === '/chat') {
        setKeyboardHasBeenClosed({})
      }
    }, [location?.pathname])

    const handleSearchPaginate = useCallback(async () => {
      const newFilterString = getFilterString(getParams(location.search))
      await dispatch.chat.loadMoreTimelinesSearch(encodeURI(newFilterString))
    }, [dispatch.chat, location.search])

    const currentParams = getParams(location.search)

    const showUnread = currentParams?.unread === '1'
    const showFavourite = currentParams?.favourite === '1'
    const setParams = (filter, val) => {
      const filterObj = getParams(location.search)
      const newObj = mergeRight(filterObj, { [filter]: val })
      const filterString = getFilterString(newObj)
      history.push({ search: filterString })
    }

    const hasChatRooms = data?.length > 0
    const firstNoApplication = hasChatRooms
      ? data.find(
          room => (!room?.applications?.id || isTourRedirect) && room?.state_machine !== 'locked'
        )
      : null
    const tourId = firstNoApplication ? firstNoApplication.timeline_room : undefined
    const hasTourId = typeof tourId !== 'undefined'

    useEffect(() => {
      if (!isTourRedirect || !hasTourId) return
      history.push(`/chat/${tourId}`)
    }, [isTourRedirect, tourId, hasTourId, history])

    const BulkActionMenu = () => {
      const selectionEmpty = selectedChat.length === 0
      return (
        <BulkActionMenuWrapper>
          <BulkActionMenuButton
            onClick={() => {
              if (selectionEmpty) return
              showMarkAsReadConfirmationModal({
                selectedChat,
                setBulkResult,
                setIsCheckAll,
                setSelectMode
              })
            }}
          >
            <MarkAsRead fill={colors.blue16} />
            {t('b.chat.view.bulk_edit.menu.mark_as_read', 'Mark as read')}
          </BulkActionMenuButton>
          <BulkActionMenuButtonDelete
            onClick={() => {
              if (selectionEmpty) return
              showDeleteConfirmationModal({ selectedChat, setBulkResult, history, setSelectMode })
            }}
          >
            <DeleteRed />
            {t('common.delete', 'Delete')}
          </BulkActionMenuButtonDelete>
        </BulkActionMenuWrapper>
      )
    }

    return (
      <SideBarWrapper
        isOpen={isOpen}
        top={top}
        className={platform}
        navigationState={navigationState}
      >
        <Wrapper>
          {hasSideBarHeader && <HeaderComponent />}
          <ControlsContainer>
            <SearchContainer isLandlord={isLandlord}>
              <SideBarSearch
                isLandlord={isLandlord}
                params={currentParams}
                setParams={setParams}
                height={isMobile ? '58px' : '65px'}
                placeholder={t('common.search', 'Search')}
                history={history}
                isMobile={isMobile}
                currentData={data}
              />
            </SearchContainer>
            {isLandlord && (!!currentParams?.filter || showUnread || showFavourite) && (
              <TagPillsContainer>
                {!!currentParams?.filter && currentParams?.filter !== 'all' && (
                  <TagPill>
                    <PillText>{capitalizeSentence(currentParams?.filter)}</PillText>
                    <PillCloseWrapper onClick={() => setParams('filter', null)}>
                      <PillClose />
                    </PillCloseWrapper>
                  </TagPill>
                )}
                {currentParams?.unread === '1' && (
                  <TagPill>
                    <PillText>{t('b.chat.view.unread.button', 'Unread')}</PillText>
                    <PillCloseWrapper onClick={() => setParams('unread', null)}>
                      <PillClose />
                    </PillCloseWrapper>
                  </TagPill>
                )}
                {currentParams?.favourite === '1' && (
                  <TagPill>
                    <PillText>{t('b.chat.view.favourites.button', 'Favourites')}</PillText>
                    <PillCloseWrapper onClick={() => setParams('favourite', null)}>
                      <PillClose />
                    </PillCloseWrapper>
                  </TagPill>
                )}
              </TagPillsContainer>
            )}
          </ControlsContainer>
          <ChatCardsContainer
            onEndReached={isSearchMode ? handleSearchPaginate : onEndReached}
            id={data.id}
            onClick={closeNav}
            hasChats={hasChatRooms}
            selectMode={selectMode}
            ref={ref}
          >
            {loading && renderChatCardSkeletons()}
            {hasChatRooms
              ? data?.map(timeline => {
                  const highlightPath = timeline?.last_event_message?.highlight
                  const highlightObj = isSearch
                    ? {
                        partial: highlightPath?.['message.ngram3']?.[0],
                        fullMatch: highlightPath?.message?.[0],
                        sender_name: highlightPath?.sender_name?.[0]
                      }
                    : null
                  return (
                    <ChatCardUser
                      highlightObj={highlightObj}
                      key={timeline?.timeline_room + '-' + timeline?.last_event_message?.id}
                      timeline={timeline}
                    />
                  )
                })
              : !data?.support &&
                !loading && (
                  <NoChatsText>
                    {t(
                      'b.chat.view.no_chats_available.text',
                      'No results found. Try a different search or remove filters.'
                    )}
                  </NoChatsText>
                )}
          </ChatCardsContainer>
          <Highlight onClick={() => history.push(`/chat/${tourId}`)} hide={!hasTourId}>
            <ChatTarget className="chat-target" />
          </Highlight>
          {chatFooterButton && <FooterButton />}
          {selectMode && <BulkActionMenu />}
        </Wrapper>
      </SideBarWrapper>
    )
  }
)

const Highlight = ({ children, onClick, hide: propHide }) => {
  const { hide, skip, next } = useHighlight('application.invite', 'sidebar', 'invite_apply')
  const { t } = useTranslation()
  const delay = useDelayedMount(1000)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const onBeforeClose = canceled => {
    if (!canceled) {
      next()
    } else {
      skip()
    }
    if (!canceled) onClick()
  }
  const steps = useMemo(
    () =>
      delay
        ? [
            {
              selector: '.chat-target',
              content: ({ next, cancel }) => (
                <Stack gutter={16} maxWidth={216}>
                  <Stack gutter={4}>
                    <Label fontSize="14px">
                      {t(
                        'b.toaster.view.application.invite.sidebar.tour.title',
                        'Choose a prospective tenant'
                      )}
                    </Label>
                    <Box color={colors.grey32}>
                      <Label fontSize="12px" type="small">
                        {t(
                          'b.toaster.view.application.invite.sidebar.tour.description',
                          'From your list of inquiries, select a prospective tenant that you might like.'
                        )}
                      </Label>
                    </Box>
                  </Stack>
                  <Box width="100%" display="flex" justify="flex-end">
                    <Box>
                      <Button size="small" theme="text" onClick={cancel}>
                        {t('common.skip', 'Skip')}
                      </Button>
                    </Box>
                    <Box width={96}>
                      <Button size="small" onClick={next}>
                        {t('common.next', 'Next')}
                      </Button>
                    </Box>
                  </Box>
                </Stack>
              )
            }
          ]
        : [],
    [t, delay]
  )
  return (
    <Tour
      hide={hide || propHide || !delay}
      steps={steps}
      point={displayMobile ? 'bottom' : 'left'}
      position={displayMobile ? 'top' : 'right'}
      positionStrategy="none"
      showCloseButton
      onBeforeClose={onBeforeClose}
    >
      {children}
    </Tour>
  )
}

export default memo(ChatSideBar)
